/**
 * Claude for Sales — the sections that have no equivalent in the Cowork,
 * Design or Code premium sets. Everything else on the page comes from the
 * shared premium template (see PREMIUM_LAYOUT_BY_SERIES in event-detail.tsx).
 *
 * Source of truth for the substance: Ronnie Cardno's curriculum brief
 * (claude.ai/code/artifact/af9bbfa3-cd51-4506-bbdb-b1e8e753f7a9), which sets
 * the page's section order as excite → prove → build trust → remove friction
 * → act.
 *
 * ── LAYOUT PROVENANCE ──────────────────────────────────────────────────────
 * These used to be headline + paragraph + a hairline grid, which read as text
 * set on a page rather than as designed sections. The compositions now come
 * from the Claude Code for Kids pages, which solved the same problem:
 *   · #fluency  — a pinned scroll-scrub scene: an anchored headline that
 *                 yields while tone-on-tone cards arrive in a stagger from the
 *                 side each will occupy.
 *
 * Three others were built and then cut from the page (Esther, 2026-08-26), all
 * in git history: #problem "Why AI hasn't helped yet" on KidsMyths' sticky
 * lede; #who "Who Should Attend" on KidsSafety's asymmetric pair, whose heading
 * moved onto the persona carousel instead; and #keep "On your desktop by the
 * time you log off", a 3x2 bento with media plates behind glass.
 *
 * What is borrowed is the COMPOSITION and the MOTION, never the materials.
 * The kids pages are built from `--kids-jewel-*`, 22px radii and a crayon
 * hand; this page is the 2026 premium system — paper #F9F9F2, the five dark
 * tone-on-tone families, 10px radius, glass built from a 158deg wash plus a
 * lit top edge, and Crayonize only ever as an aside. Carrying the kids palette
 * across would have made these sections look like a different website.
 *
 * OWNED FILE: the layout lives in component-scoped <style> blocks here, the
 * way the kids components do it, rather than in cowork-ds.css. That file is
 * already 4,200 lines of scope overrides and this is new structure, not a
 * retint of inherited structure. Colours still come from the page's tokens so
 * the `.ds-sales` accent walk stays in charge.
 *
 * The brief's one hard rule: never the word "templates." Every output here is
 * written fresh, live, in the seller's own voice. "Template" implies
 * mail-merge and undercuts the whole premise.
 */
import { useEffect, useRef, useState } from "react";
import { SiteEyebrow } from "@/components/sections/workshop-ticket-block";

/* ═══════════════════════════════════════════════════════════════════════════
   1 · THE PROOF — real briefs, opened, not screenshotted
   The brief's killer move #1: don't show a picture of the output, hand the
   reader the output. Each entry is a live artifact from a real run.

   ⚠️ Ronnie owns these links. Until at least one lands, the whole section
   hides itself (see the filter in the component) rather than shipping a row
   of dead cards. Paste a URL into `href` and the card appears.
   ═══════════════════════════════════════════════════════════════════════════ */

type SalesBrief = {
  key: string;
  /** What the seller typed in. The whole point: this was the entire input. */
  input: string;
  /** What kind of company, so the reader can find the one nearest their market. */
  sector: string;
  /** The two or three things in it worth naming. */
  contains: string;
  /** Live artifact URL. Null hides the card. */
  href: string | null;
};

const SALES_BRIEFS: SalesBrief[] = [
  { key: "brief-1", input: "One company name", sector: "Enterprise software", contains: "Stakeholder map with LinkedIn profiles, recent signals, competitive set, every claim sourced.", href: null },
  { key: "brief-2", input: "One company name", sector: "Regional services operator", contains: "What they actually sell, how they're structured, who owns the budget, where the market says they hurt.", href: null },
  { key: "brief-3", input: "One company name", sector: "Manufacturing", contains: "Leadership changes, expansion signals, the two competitors they lose to, named contacts.", href: null },
];

export function SalesArtifactGallery() {
  const live = SALES_BRIEFS.filter((b) => b.href);
  if (live.length === 0) return null;

  return (
    <section id="proof" aria-labelledby="proof-heading">
      <SiteEyebrow label="Real output, not screenshots" />
      <h2 id="proof-heading" className="type-h2 text-charcoal mb-6" style={{ maxWidth: "var(--max-w-heading)" }}>
        These are briefs the research engine actually produced. Open one.
      </h2>
      <p className="type-body-lg text-charcoal/75 leading-relaxed mb-12" style={{ maxWidth: "660px" }}>
        Every one of them started as a company name and nothing else. No
        manual digging, no cleanup pass. Read one the way the prospect's
        competitor would, then picture it waiting for you before your next
        first call.
      </p>

      <div
        className="grid md:grid-cols-3"
        style={{ borderTop: "var(--border-structural)", borderLeft: "var(--border-structural)" }}
      >
        {live.map((brief, i) => (
          <a
            key={brief.key}
            href={brief.href!}
            target="_blank"
            rel="noopener noreferrer"
            className="group p-7 md:p-9 flex flex-col gap-5 transition-colors hover:bg-charcoal/[0.02]"
            style={{ borderRight: "var(--border-structural)", borderBottom: "var(--border-structural)" }}
          >
            <span
              className="font-mono uppercase"
              style={{ fontSize: "10.5px", letterSpacing: "1.4px", fontWeight: 500, color: "var(--terra)" }}
            >
              Brief {String(i + 1).padStart(2, "0")} · {brief.sector}
            </span>
            <span className="type-h4 text-charcoal">{brief.input} in.</span>
            <span className="type-body-sm text-charcoal/70 leading-relaxed flex-1">{brief.contains}</span>
            <span className="type-body-sm" style={{ color: "var(--terra)" }}>
              Read the brief
              <span aria-hidden="true" className="inline-block transition-transform group-hover:translate-x-1 ml-1.5">
                &rarr;
              </span>
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   2 · THE BIGGER WIN — real Claude fluency
   The brief marks this one "make prominent," and it is the argument that widens
   the page past "sales tips" and carries the price. So it gets the one piece of
   real choreography on the page.

   ── THE SHAPE ──────────────────────────────────────────────────────────────
   A pinned scroll-scrub scene: 250vh of outer runway, a one-screen stage, and a
   normalized 0-1 progress read off the wrapper's rect on a passive scroll
   listener. Every animated property is inline with `transition: none`, which is
   what makes it scrub rather than animate — scrolling back up peels the cards
   off in reverse, because nothing is running on its own clock.

   Two motions on one timeline, in opposite directions:
     · the headline anchors the centre and YIELDS, scaling 1 -> 0.88, while
     · the cards arrive in a stagger, each in its own sub-window, each entering
       from the side it will occupy (left from -60px X, right from +60px).

   ── WHAT CHANGED FROM THE ORBIT VERSION ────────────────────────────────────
   An absolutely-positioned elliptical orbit was tried and dropped (Esther,
   2026-08-26). Two columns flanking the centre is what she wanted, and it is
   also the more robust construction: the orbit needed hand-solved coordinates,
   a frozen reference canvas and a scale-to-fit, because seven text cards around
   a centre have almost no packing slack and any viewport change broke them.
   Flex columns simply reflow. The orbit's coordinates and the solver notes are
   in git history if it is ever wanted back.

   SIX CARDS, NOT SEVEN. Two columns want an even count. "Directing the AI" came
   out rather than an eighth being invented: it described prompt technique, which
   is the one thing on the list that is not a Claude capability, and it sat
   closest to the "prompt tips" this section exists to distance itself from.

   ── A DARK PLUM ROOM WITH LIGHTER PLUM CARDS ───────────────────────────────
   This ran as five different dark families on paper, one per card, solved by
   hand against the system's adjacency rules. Esther asked for the whole section
   to be tone-on-tone plum: a purple ground with lighter purple cards on it
   (2026-08-27). So the room is dark plum and the cards step UP out of it, which
   is the reverse of every other card group on this page.

   THREE VALUES OF ONE HUE. Contrast decides them, not taste:

     room   #2C1B2A   headline #E7D6E2 on it reads 11.65
     card   #4A2D44   1.35 above the room; body #CDAFC4 on it reads 6.01
     card   #5F3C54   1.74 above the room; body #CDAFC4 on it reads 4.66

   The ceiling is real. Plum 500 (#7A5069) would separate best at 1.99 but its
   body contrast is 3.31, and #6A4460 is 4.03 — both fail AA, so the lightest
   card stops at #5F3C54, which is the lightest plum measured to still clear
   4.5 for body text. That is also why the room went deeper than the page's own
   #3B2438: with #3B2438 as ground the only passing cards sit 1.17 and 1.39
   above it, which is not a difference you can see.

   TWO STEPS, NOT THREE. #58374F and #5F3C54 are 1.60 and 1.74 above the room —
   close enough that two cards carrying them side by side read as a rendering
   artefact rather than a decision. Two well-separated steps, checkerboarded so
   no two neighbours match on either axis of the 3x2:

           left              right
     row 1  mid    #4A2D44    lift   #5F3C54
     row 2  lift   #5F3C54    mid    #4A2D44
     row 3  mid    #4A2D44    lift   #5F3C54

   Title, body and the glass wash are identical on every card. Holding the type
   constant while the field moves is what makes six cards read as one set.

   NO DROP SHADOW. The cards carried `0 22px 44px -30px rgba(20,20,20,.32)`,
   which did its job over paper and does nothing over a ground darker than the
   shadow. The lit top edge carries the lift instead.

   ONE CONSEQUENCE WORTH KNOWING: gold lived here and nowhere else, so the page
   now has no gold on it at all. The brief wanted the accent rotated across the
   five dark families; that is deliberately not what this page does any more.
   ═══════════════════════════════════════════════════════════════════════════ */

/** One plum step: the field it sits on, plus the family's light steps for type.
 *  `title`, `body` and `wash` are identical across all four on purpose — only
 *  the field moves, which is what tone-on-tone means. */
type Tone = { field: string; title: string; body: string; wash: string };
const PLUM_TITLE = "#E9D8E4";  // plum 900
const PLUM_BODY = "#CDAFC4";   // plum 800
const PLUM_WASH = "233, 216, 228";
/** The room the cards sit in. Deeper than the page's #3B2438 on purpose — see
 *  the note above; #3B2438 leaves no visible headroom for a lighter card. */
const PLUM_ROOM = "#2C1B2A";
const step = (field: string): Tone => ({ field, title: PLUM_TITLE, body: PLUM_BODY, wash: PLUM_WASH });
const TONES: Record<string, Tone> = {
  mid:  step("#4A2D44"),
  lift: step("#5F3C54"),
};

/* `icon` names a PAINTED subject in ds-icons.tsx, chosen against the item's own
   sentence rather than its category (stamp = repeat-on-command, seal = a
   finished document as against `checklist`/`deck` which are working paper).
   `side` is the column it occupies AND the direction it enters from. */
/* THE FEATURE NAMES ARE GONE, AND THAT IS THE POINT (Esther, 2026-08-31).
   This list used to read Skills / Connectors / Artifacts / The desktop app /
   Memory and projects / What comes next — which is Claude's own feature
   taxonomy, not sales work. A seller landing here had to translate every label
   before it meant anything.

   Each item now leads with what it lets you DO and mentions the feature only
   where the name is worth knowing. Same six capabilities, same order, same
   sides and tone steps.

   `side` is the column it occupies AND the direction it enters from. `icon` is
   retained on each item but no longer rendered — the marks came out on Isaac's
   note that they were distracting. */
const FLUENCY = [
  { term: "Save it and reuse it", line: "Turn any job you have done once into something you run again with a word. This is how the research and the outreach stay with you after today.", icon: "stamp", side: "left", tone: "mid" },
  { term: "Connect your inbox and your CRM", line: "Claude reads from the tools your account information already lives in, so you stop pasting things into a chat window.", icon: "plug", side: "right", tone: "lift" },
  { term: "Documents you would actually send", line: "Turn a brief or a proposal into something a buyer can open, rather than a wall of chat text.", icon: "seal", side: "left", tone: "lift" },
  { term: "It is an app on your machine", line: "No terminal, no code, nothing to install twice.", icon: "laptop", side: "right", tone: "mid" },
  { term: "It remembers your accounts", line: "Come back next week and it still knows your business and the deals you are working.", icon: "cardindex", side: "left", tone: "mid" },
  { term: "Where this goes next", line: "A look at work that runs while you are on a call. So you know the road, not so you build it today.", icon: "signpost", side: "right", tone: "lift" },
] as const;

const RUNWAY_VH = 250;     // outer height; the stage is one screen tall
const FIRST_IN = 0.15;     // where the first card starts arriving
const LAST_OUT = 0.90;     // where the last one has fully landed
const WINDOW = 0.30;       // each card's own slice of the timeline
const ENTER_X = 60;        // px outward along its own side at t=0
const ENTER_LIFT = 18;     // px below its resting place at t=0, so it fades up
/* The two fixed bars above this section, measured rather than guessed:
   nav 73px + .ds-jumpnav ticket bar 79px. The stage is inset by this so the top
   of the composition is not parked underneath them. */
const TOP_INSET = 152;
const PIN_MIN_WIDTH = 1024;
const PIN_MIN_HEIGHT = 760;

/** Remap `p` from [a, b] onto [0, 1], clamped. */
function remap(p: number, a: number, b: number) {
  return Math.min(1, Math.max(0, (p - a) / (b - a)));
}

/** The sub-window for card `i` of `n`, evenly staggered across the runway. */
function windowFor(i: number, n: number): [number, number] {
  const lastStart = LAST_OUT - WINDOW;
  const step = n > 1 ? (lastStart - FIRST_IN) / (n - 1) : 0;
  const start = FIRST_IN + i * step;
  return [start, start + WINDOW];
}

/**
 * Can this viewport carry the pinned scene? Width, height and reduced-motion
 * only — deliberately NOT anything measured off the section itself. Folding
 * that in created a circular dependency once: progress started null, so the
 * first render was the static branch, so the effect measured the element it was
 * about to replace.
 */
function useCanPin() {
  const [canPin, setCanPin] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(
      `(min-width: ${PIN_MIN_WIDTH}px) and (min-height: ${PIN_MIN_HEIGHT}px)`,
    );
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const read = () => setCanPin(mq.matches && !reduce.matches);
    read();
    mq.addEventListener("change", read);
    reduce.addEventListener("change", read);
    return () => {
      mq.removeEventListener("change", read);
      reduce.removeEventListener("change", read);
    };
  }, []);
  return canPin;
}

/**
 * Normalized 0-1 progress through the pinned wrapper.
 *
 * Read off the wrapper's own bounding rect rather than a cached offsetTop: the
 * rect stays correct after any reflow above this section, which on this page
 * happens whenever lazy imagery loads. Passive listener, rAF-deferred write.
 */
function usePinnedProgress(ref: React.RefObject<HTMLElement | null>, active: boolean) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!active || typeof window === "undefined") return;
    let raf = 0;
    const read = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const travel = r.height - window.innerHeight;
      setProgress(travel <= 0 ? 1 : Math.min(1, Math.max(0, -r.top / travel)));
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    /* rAF does not run while the document is hidden, so a tab scrolled in the
       background and restored would paint whatever frame it last managed. */
    document.addEventListener("visibilitychange", onScroll);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("visibilitychange", onScroll);
    };
  }, [ref, active]);
  return progress;
}

function FluencyCard({
  item,
  index,
  progress,
}: {
  item: (typeof FLUENCY)[number];
  index: number;
  progress: number | null;
}) {
  const [from, to] = windowFor(index, FLUENCY.length);
  const t = progress === null ? 1 : remap(progress, from, to);
  const dir = item.side === "left" ? -1 : 1;
  const tone = TONES[item.tone];

  return (
    <div
      className="sf-card"
      style={
        {
          opacity: t,
          transform: `translate3d(${(1 - t) * ENTER_X * dir}px, ${(1 - t) * ENTER_LIFT}px, 0)`,
          transition: "none",
          "--tone-field": tone.field,
          "--tone-title": tone.title,
          "--tone-body": tone.body,
          "--tone-wash": tone.wash,
        } as React.CSSProperties
      }
    >
      <div>
        <h3 className="sf-term">{item.term}</h3>
        <p className="sf-line">{item.line}</p>
      </div>
    </div>
  );
}

export function SalesFluencySection() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const pinned = useCanPin();
  const progress = usePinnedProgress(wrapRef, pinned);
  // The anchor yields as the cards arrive: 1 -> 0.88 across the whole runway.
  const headScale = pinned ? 1 - 0.12 * progress : 1;

  const left = FLUENCY.filter((f) => f.side === "left");
  const right = FLUENCY.filter((f) => f.side === "right");

  // Typed against the general item, not `typeof left`. FLUENCY is `as const`,
  // so each entry narrows to its own literal type and the two filtered arrays
  // end up with incompatible unions.
  const cards = (col: readonly (typeof FLUENCY)[number][]) =>
    col.map((item) => (
      <FluencyCard
        key={item.term}
        item={item}
        index={FLUENCY.indexOf(item)}
        progress={pinned ? progress : null}
      />
    ));

  const anchor = (
    <div className="sf-anchor" style={{ transform: `scale(${headScale})`, transition: "none" }}>
      <h2 id="fluency-heading" className="type-h2 sf-h2">
        You'll actually learn Claude, not just this one workflow.
      </h2>
    </div>
  );

  return (
    <section
      id="fluency"
      aria-labelledby="fluency-heading"
      className="relative"
      style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
    >
      <style>{`
        /* The room is DARK PLUM and the cards are lighter plum on top of it —
           the reverse of the paper room this section used to be. */
        #fluency { background: ${PLUM_ROOM}; }

        .sf-runway { position: relative; }
        /* NOT position: sticky. The page wrapper carries overflow-x: clip
           (div.min-h-screen.bg-cream.overflow-x-clip), which Chrome treats as
           the sticky containing context, so a sticky child never engages — it
           measured y = -1456 instead of pinning at 0. Pinning is driven from
           progress instead: the stage sits absolutely at the runway's top and
           translates down by progress x (runway - one screen), while the
           runway's own top sits at minus that same travel. They cancel, so the
           stage holds the viewport exactly. Immune to any ancestor's overflow. */
        .sf-stage {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          height: 100vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: ${TOP_INSET}px clamp(20px, 3vw, 56px) 0;
        }
        .sf-scene {
          width: 100%;
          max-width: 1500px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.22fr) minmax(0, 1fr);
          gap: clamp(16px, 2.2vw, 40px);
          align-items: center;
        }
        .sf-col { display: flex; flex-direction: column; gap: clamp(14px, 1.5vw, 24px); }

        /* The anchor keeps its full section-heading size — the whole point of
           the yield is that something large gives ground. */
        .sf-anchor { text-align: center; transform-origin: 50% 50%; will-change: transform; }
        .sf-h2 {
          margin: 0 auto;
          max-width: 17ch;
          color: var(--plum-900, #E7D6E2) !important;
          text-wrap: balance;
        }

        /* ONE COLUMN, NO ICON. Isaac's note on the Code page: the icons were
           distracting, and taking them out widens the card and lets the term be
           bigger. The 40px icon track and its gap are gone, which hands ~56px
           of measure back to the copy at every card width. */
        .sf-card {
          display: block;
          padding: clamp(16px, 1.5vw, 24px);
          border-radius: 10px;
          /* Each card is its own family: the page's glass wash in that family's
             light step, over that family's field, with a lit top edge. */
          background:
            linear-gradient(158deg,
              rgba(var(--tone-wash), 0.11) 0%,
              rgba(var(--tone-wash), 0.055) 46%,
              rgba(var(--tone-wash), 0.03) 100%),
            var(--tone-field);
          border: 1px solid rgba(var(--tone-wash), 0.20);
          border-top-color: rgba(var(--tone-wash), 0.44);
          will-change: transform, opacity;
        }
        .sf-term {
          font-family: var(--font-display), system-ui, sans-serif;
          font-weight: 400;
          font-size: clamp(19px, 1.6vw, 25px);
          line-height: 1.14;
          color: var(--tone-title);
          margin: 0 0 8px;
          text-wrap: balance;
        }
        .sf-line {
          font-size: clamp(13px, 0.92vw, 14.5px);
          line-height: 1.5;
          color: var(--tone-body);
          margin: 0;
        }

        /* ── The static fallback, below the pinning gate ────────────────────
           Same cards, same steps, same copy, no choreography. */
        .sf-static { padding: clamp(88px, 9vw, 140px) clamp(24px, 5vw, 80px); }
        .sf-static-inner { max-width: 1180px; margin: 0 auto; }
        .sf-static .sf-anchor { text-align: left; transform: none !important; }
        .sf-static .sf-h2 { margin-left: 0; max-width: 24ch; }
        .sf-bento {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: clamp(14px, 1.4vw, 22px);
          margin-top: clamp(32px, 3.6vw, 52px);
        }
        @media (max-width: 639px) {
          .sf-bento { grid-template-columns: 1fr; }
          /* ONE COLUMN NEEDS ITS OWN ALTERNATION. The step map is solved for a
             two-column grid, and at two columns it holds in both layouts — the
             pinned 3x2 and this bento. Serialised into ONE column it pairs up:
             Connectors and Artifacts are both the lift step, the desktop app and
             Memory are both the mid step, so two neighbours match.

             That is not a bad map, it is arithmetic. With two steps and sides
             that alternate, taking every other item out of an alternating
             sequence gives a constant, so alternating down one column and
             alternating across two columns cannot both hold. The phone gets the
             field forced instead. !important because the component sets
             --tone-field inline, and inline wins otherwise. */
          .sf-bento .sf-card:nth-child(odd) { --tone-field: #4A2D44 !important; }
          .sf-bento .sf-card:nth-child(even) { --tone-field: #5F3C54 !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sf-card { opacity: 1 !important; transform: none !important; }
        }
      `}</style>

      {pinned ? (
        <div className="sf-runway" ref={wrapRef} style={{ height: `${RUNWAY_VH}vh` }}>
          <div
            className="sf-stage"
            style={{
              transform: `translateY(${progress * (RUNWAY_VH - 100)}vh)`,
              transition: "none",
            }}
          >
            <div className="sf-scene">
              <div className="sf-col">{cards(left)}</div>
              {anchor}
              <div className="sf-col">{cards(right)}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="sf-static" ref={wrapRef}>
          <div className="sf-static-inner">
            {anchor}
            <div className="sf-bento">
              {FLUENCY.map((item, i) => (
                <FluencyCard key={item.term} item={item} index={i} progress={null} />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
